import { useState, type CSSProperties, type SyntheticEvent } from 'react';
import { AlertOctagon, AudioLines, FileQuestion, Film, Loader2 } from 'lucide-react';
import { formatDuration, spriteTileAt, type AssetSummary, type SpriteLayout } from '@prism/contracts';

/** Percentage-based sprite positioning: the element can be any size with the tile's aspect ratio. */
export function spriteStyle(url: string, layout: SpriteLayout, index: number): CSSProperties {
  const i = Math.max(0, Math.min(layout.count - 1, index));
  const col = i % layout.columns;
  const row = Math.floor(i / layout.columns);
  const x = layout.columns > 1 ? (col / (layout.columns - 1)) * 100 : 0;
  const y = layout.rows > 1 ? (row / (layout.rows - 1)) * 100 : 0;
  return {
    aspectRatio: `${layout.tileWidth} / ${layout.tileHeight}`,
    backgroundImage: `url("${url}")`,
    backgroundSize: `${layout.columns * 100}% ${layout.rows * 100}%`,
    backgroundPosition: `${x}% ${y}%`,
  };
}

/** An image's fade-in (1.2.6): data-loaded once it has loaded or failed, or before the first paint when already in memory. Also the Library list's thumbnails (1.2.8). */
export const markLoaded = (e: SyntheticEvent<HTMLImageElement>) => { e.currentTarget.dataset.loaded = ''; };
export const markIfComplete = (img: HTMLImageElement | null) => { if (img?.complete) img.dataset.loaded = ''; };

/** Poster with hover-to-scrub using the sprite sheet. */
export function AssetThumb({ asset, scrub = true }: { asset: AssetSummary; scrub?: boolean }) {
  const [hover, setHover] = useState<number | null>(null);
  const durationSec = (asset.durationTicks ?? 0) / 705_600_000;
  const layout = asset.spriteLayout;

  let content;
  if (asset.status === 'placeholder') {
    content = (
      <div className="thumb-placeholder empty-asset" data-testid="placeholder-thumb">
        <div className="stack"><FileQuestion size={22} /><span>No media yet</span></div>
      </div>
    );
  } else if (asset.status === 'failed' && !asset.thumbnailUrl) {
    content = (
      <div className="thumb-placeholder failed">
        <div className="stack"><AlertOctagon size={22} /><span>{asset.error?.code === 'INVALID_MEDIA' ? 'Unreadable media' : 'Processing failed'}</span></div>
      </div>
    );
  } else if (!asset.thumbnailUrl) {
    const processing = asset.status === 'processing' || asset.status === 'registered';
    content = (
      <div className="thumb-placeholder">
        <div className="stack">
          {processing ? <Loader2 size={20} className="spin" /> : asset.mediaKind === 'audio' ? <AudioLines size={24} /> : <Film size={22} />}
          <span>{processing ? asset.progress?.label ?? 'Queued' : asset.mediaKind === 'audio' ? 'Audio' : 'No preview'}</span>
        </div>
      </div>
    );
  } else {
    // Fades in when it has loaded (1.2.6, CSS .thumb > img). One already in memory shows at once (marked before the
    // first paint), and one that fails is shown as before (marked on error too). Keyed by its URL so a new poster fades.
    content = <img key={asset.thumbnailUrl} src={asset.thumbnailUrl} alt="" loading="lazy" draggable={false}
      ref={markIfComplete} onLoad={markLoaded} onError={markLoaded} />;
  }

  const canScrub = scrub && asset.spriteUrl && layout && durationSec > 0;
  return (
    <div
      className="thumb"
      onMouseMove={canScrub ? (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setHover(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)));
      } : undefined}
      onMouseLeave={() => setHover(null)}
    >
      {content}
      {asset.durationTicks != null && hover === null && <span className="duration">{formatDuration(asset.durationTicks)}</span>}
      {canScrub && hover !== null && (
        <>
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: '#000' }}>
            <div style={{ ...spriteStyle(asset.spriteUrl!, layout!, spriteTileAt(layout!, hover * durationSec)), maxWidth: '100%', maxHeight: '100%', height: '100%' }}
              data-testid="scrub-frame" data-tile={spriteTileAt(layout!, hover * durationSec)} />
          </div>
          <div className="scrub-line" style={{ left: `${hover * 100}%` }} />
        </>
      )}
    </div>
  );
}
